import { useEffect, useRef } from 'react';
import mqtt, { MqttClient } from 'mqtt';
import axios from 'axios';

interface RabbitConfigDTO {
  host: string;
  username: string;
  password: string;
  port: number;
}

interface SiteInfoDTO {
  siteuuid: string;
  sitename: string;
  siteid: number;
}

const PUBLIC_URL = '/api/public/patientcallercontroller';

async function fetchRabbitConfig(): Promise<RabbitConfigDTO> {
  const response = await axios.get<RabbitConfigDTO>(`${PUBLIC_URL}/fetchRabbitConfig`);
  return response.data;
}

async function fetchSiteInfo(): Promise<SiteInfoDTO> {
  const response = await axios.get<SiteInfoDTO>(`${PUBLIC_URL}/loadSiteInfo`);
  return response.data;
}

/**
 * Hook that subscribes to MQTT agenda updates for a professional and/or
 * selected specialities. Calls onUpdate (debounced) when a message arrives.
 */
export function useAgendaMqtt(
  professionalId: number | null,
  specialityIds: number[],
  onUpdate: () => void,
  debounceMs: number = 1500
) {
  const clientRef = useRef<MqttClient | null>(null);
  const topicsRef = useRef<string[]>([]);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const specialityIdsKey = specialityIds.join(',');

  useEffect(() => {
    if (!professionalId && specialityIds.length === 0) return;

    let cancelled = false;

    async function connect() {
      try {
        const [config, siteInfo] = await Promise.all([
          fetchRabbitConfig(),
          fetchSiteInfo(),
        ]);

        if (cancelled) return;

        const topics = [
          ...(professionalId ? [`${siteInfo.siteuuid}-agenda-${professionalId}`] : []),
          ...specialityIds.map((id) => `${siteInfo.siteuuid}-agenda-speciality-${id}`),
        ];
        const wsUrl = `wss://${config.host}:${config.port}/ws`;

        const client = mqtt.connect(wsUrl, {
          clientId: `agenda_${professionalId ?? 'service'}_${specialityIdsKey || 'none'}_${Math.random().toString(16).slice(2)}`,
          username: config.username,
          password: config.password,
          reconnectPeriod: 5000,
          connectTimeout: 4000,
        });

        client.on('connect', () => {
          client.subscribe(topics);
        });

        client.on('message', () => {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          debounceTimerRef.current = setTimeout(() => {
            if (!cancelled) onUpdateRef.current();
          }, debounceMs);
        });

        clientRef.current = client;
        topicsRef.current = topics;
      } catch (err) {
        console.warn('Could not connect to MQTT for agenda updates:', err);
      }
    }

    connect();

    return () => {
      cancelled = true;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (clientRef.current) {
        if (topicsRef.current.length > 0) {
          clientRef.current.unsubscribe(topicsRef.current);
        }
        clientRef.current.end();
        clientRef.current = null;
        topicsRef.current = [];
      }
    };
  }, [professionalId, specialityIdsKey, debounceMs]);
}
